// File: frontend/src/pages/STIXBundlesPage.tsx
import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  styled,
  alpha,
  CircularProgress,
  Link
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { useStixBundles } from '../hooks/useStixBundles';
import { BundleStatsCards } from '../components/STIX/BundleStatsCards';
import { BundleFilters } from '../components/STIX/BundleFilters';
import { BundleTable } from '../components/STIX/BundleTable';
import { BundlePreviewModal } from '../components/STIX/BundlePreviewModal';
import { IntegrationGuidesSidebar } from '../components/STIX/IntegrationGuidesSidebar';
import { STIXBundleEnriched, BundleFilters as BundleFiltersType } from '../types/stix';
import { cyber } from '../theme';
import { publications } from '../data/publications';

const HeroSection = styled(Box)(({ theme }) => ({
  background: cyber.gradient,
  border: `1px solid ${alpha(cyber.primaryLight, 0.35)}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(5),
  marginBottom: theme.spacing(4),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '50%',
    filter: 'blur(60px)',
  }
}));

const HeroContent = styled(Box)({
  position: 'relative',
  zIndex: 1
});

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '16px',
  background: alpha(theme.palette.common.white, 0.2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

export const STIXBundlesPage: React.FC = () => {
  const {
    bundles,
    statistics,
    loading,
    error,
    fetchBundle,
    downloadBundle,
    applyFilters
  } = useStixBundles();

  const [selectedBundle, setSelectedBundle] = useState<STIXBundleEnriched | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const handlePreviewBundle = (bundle: STIXBundleEnriched) => {
    setSelectedBundle(bundle);
    setPreviewModalOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setSelectedBundle(null);
  };

  const handleFilterChange = (filters: BundleFiltersType) => {
    applyFilters(filters);
  };

  // Get latest bundle date for stats card
  const latestUpdate = bundles.length > 0 ? bundles[0].createdDate : undefined;

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      {/* Hero Header */}
      <HeroSection>
        <HeroContent>
          <Box display="flex" alignItems="center" mb={2}>
            <IconWrapper>
              <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />
            </IconWrapper>
            <Box ml={2}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                📦 STIX 2.1 Bundles
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Structured Threat Intelligence for your security stack
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                Generation approach described in peer-reviewed research:{' '}
                <Link href={publications[0].url} target="_blank" rel="noopener noreferrer" color="inherit" underline="always">
                  {publications[0].venue}, {publications[0].year}
                </Link>
              </Typography>
            </Box>
          </Box>
        </HeroContent>
      </HeroSection>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <BundleStatsCards
        statistics={statistics}
        loading={loading}
        latestUpdate={latestUpdate}
      />

      {/* Main Content Layout */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Left Column: Filters + Table */}
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 66%' } }}>
          {/* Filters */}
          <BundleFilters onFilterChange={handleFilterChange} />

          {/* Loading State */}
          {loading && !bundles.length && (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress size={60} />
            </Box>
          )}

          {/* Bundles Table */}
          {!loading || bundles.length > 0 ? (
            <>
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Showing <Box component="span" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono, fontWeight: 700 }}>{bundles.length}</Box> bundle{bundles.length !== 1 ? 's' : ''}
                  {statistics && (
                    <> of <Box component="span" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono, fontWeight: 700 }}>{statistics.total_bundles}</Box> total</>
                  )}
                </Typography>
              </Box>
              <BundleTable
                bundles={bundles}
                onPreview={handlePreviewBundle}
                onDownload={downloadBundle}
              />
            </>
          ) : null}
        </Box>

        {/* Right Column: Integration Guides */}
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 33%' } }}>
          <IntegrationGuidesSidebar />
        </Box>
      </Box>

      {/* Preview Modal */}
      <BundlePreviewModal
        open={previewModalOpen}
        bundle={selectedBundle}
        onClose={handleClosePreview}
        onFetchBundle={fetchBundle}
        onDownload={downloadBundle}
      />
    </Container>
  );
};

export default STIXBundlesPage;
